import axios from 'axios';
import { BACKEND_BASE_URL } from '../config';  

const api = axios.create({
  baseURL: BACKEND_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to get the auth token
const getAuthToken = () => localStorage.getItem('authToken');

// Function to remove auth tokens and logout user
const logoutUser = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  window.location.href = '/login';  // Redirect to login page
};

// Function to refresh the token
const refreshToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token found');
    }

    const response = await axios.post(
      `${BACKEND_BASE_URL}/api/token/refresh/`,
      { refresh: refreshToken },
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const { access } = response.data;
    localStorage.setItem('authToken', access);
    return access;
  } catch (error) {
    console.error('Refresh token expired or invalid', error);
    logoutUser();  // Logout when refresh token is invalid
    throw new Error('Token refresh failed');
  }
};

// Attach auth token to every request
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 errors and refresh token if needed
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If access token expired (401) → try refresh token (once)
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await refreshToken();   // <– refresh
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return api(originalRequest);                   // <– retry original request
      } catch (refreshError) {
        // Refresh token ALSO failed → forced logout
        console.error("Refresh token failed → forced logout");
        logoutUser();
        return Promise.reject(refreshError);
      }
    }

    // If refresh token ALSO causes 401 or 403 → logout
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      logoutUser();
    }

    return Promise.reject(error);
  }
);


export default api;
